import { useState, useEffect, useRef } from 'react';
import {
  Shield, CheckCircle2, XCircle, ChevronRight, RefreshCw, AlertCircle,
  SkipForward, Camera
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const VMS_TYPES = ['WAVE_VMS', 'GENETEC_VMS', 'MILESTONE_VMS', 'MIRASYS_VMS', 'AVIGILON_VMS', 'VERKADA_VMS'];

const RecordingAssuranceStep = ({ onNext }) => {
  const { savedData, prerequisites, markStepComplete } = useSiteSetupStore();

  const [loading, setLoading] = useState(true);
  const [vmsIntegrations, setVmsIntegrations] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [camLoading, setCamLoading] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSkippedRef = useRef(false);

  // Initial load: check for VMS integrations; auto-skip if none
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithTenant('/api/integrations');
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (data.integrations || []);
        const vms = list.filter(i => i.enabled && VMS_TYPES.includes(i.type));
        setVmsIntegrations(vms);

        if (vms.length === 0) {
          autoSkippedRef.current = true;
          await markStepComplete('recording_assurance', { skipped: true, reason: 'no-vms' });
          onNext();
          return;
        }
        setSelectedIntegrationId(vms[0].id);
      } catch (err) {
        console.error('[RA] load integrations failed:', err);
        setError(err.message || 'Failed to load integrations');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Load cameras when integration changes
  useEffect(() => {
    if (!selectedIntegrationId) return;
    (async () => {
      setCamLoading(true);
      setCameras([]);
      try {
        const res = await fetchWithTenant(`/api/vms/integrations/${selectedIntegrationId}/cameras`);
        if (res.ok) {
          const data = await res.json();
          const list = data.cameras || data || [];
          setCameras(list);
          setSelected(new Set(list.map(c => c.id)));
        }
      } catch (err) {
        console.error('[RA] load cameras failed:', err);
      } finally {
        setCamLoading(false);
      }
    })();
  }, [selectedIntegrationId]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === cameras.length) setSelected(new Set());
    else setSelected(new Set(cameras.map(c => c.id)));
  };

  const handleEnable = async () => {
    if (selected.size === 0 || !selectedIntegrationId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithTenant('/api/recording-assurance/config/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationId: selectedIntegrationId,
          cameraIds: Array.from(selected),
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to enable Recording Assurance');
      }
      const data = await res.json();
      setResult({ enabled: data.enabled || 0, skipped: data.skipped || 0 });
      await markStepComplete('recording_assurance', {
        enabled: true,
        cameraCount: data.enabled || 0,
        integrationId: selectedIntegrationId,
      });
    } catch (err) {
      console.error('[RA] enable failed:', err);
      setError(err.message || 'Failed to enable Recording Assurance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    await markStepComplete('recording_assurance', { skipped: true });
    onNext();
  };

  if (autoSkippedRef.current || loading) {
    return (
      <div className="site-setup-step">
        <div className="verify-loading">
          <RefreshCw size={20} className="spin" />
          <p>Checking Recording Assurance prerequisites...</p>
        </div>
      </div>
    );
  }

  const hasAgent = !!prerequisites?.hasConnectedAgent;
  const hasSwitch = !!prerequisites?.hasSwitch;
  const hasVms = vmsIntegrations.length > 0;
  const allGood = hasAgent && hasVms; // switch is a warning, not a blocker

  // Results screen
  if (result) {
    return (
      <div className="site-setup-step recording-assurance-step">
        <h3>Recording Assurance Enabled</h3>
        <div className="scan-summary success">
          <CheckCircle2 size={20} />
          <div>
            <strong>
              {result.enabled} camera{result.enabled === 1 ? '' : 's'} now being monitored for recording health.
            </strong>
            {result.skipped > 0 && (
              <p>{result.skipped} skipped (license limit or already enabled).</p>
            )}
          </div>
        </div>
        <div className="step-actions">
          <button className="site-setup-btn primary" onClick={onNext}>
            Continue <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="site-setup-step recording-assurance-step">
      <h3>Recording Assurance</h3>
      <p className="step-description">
        Continuously verify that every camera is recording, not just streaming. We'll monitor the full chain from camera to storage.
      </p>

      {error && (
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="setup-form-section">
        <h4><Shield size={14} /> Prerequisites</h4>
        <ul className="ra-prereq-list">
          <li className={hasVms ? 'ok' : 'bad'}>
            {hasVms ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <div>
              <strong>VMS Integration</strong>
              <span>{hasVms ? `${vmsIntegrations.length} configured` : 'No VMS integration configured'}</span>
            </div>
          </li>
          <li className={hasAgent ? 'ok' : 'bad'}>
            {hasAgent ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <div>
              <strong>Agent Connected</strong>
              <span>{hasAgent ? 'Agent is online' : 'Agent is offline'}</span>
            </div>
          </li>
          <li className={hasSwitch ? 'ok' : 'warn'}>
            {hasSwitch ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <div>
              <strong>Network Switch</strong>
              <span>
                {hasSwitch
                  ? 'Managed switch detected'
                  : 'No managed switch detected (optional, improves chain visibility)'}
              </span>
            </div>
          </li>
        </ul>
      </div>

      {!allGood ? (
        <>
          <p className="setup-hint">
            Some prerequisites aren't met. You can still enable Recording Assurance later from the Recording Assurance module once the missing items are in place.
          </p>
          <div className="step-actions">
            <button className="site-setup-btn primary" onClick={handleSkip}>
              <SkipForward size={14} /> Skip for now <ChevronRight size={16} />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="setup-form-section">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <strong style={{ color: '#e5e7eb' }}>Enable Recording Assurance</strong>
            </label>
          </div>

          {enabled && (
            <>
              {vmsIntegrations.length > 1 && (
                <div className="setup-form-section">
                  <div className="setup-form-field full-width">
                    <label>VMS</label>
                    <select
                      value={selectedIntegrationId}
                      onChange={(e) => setSelectedIntegrationId(e.target.value)}
                    >
                      {vmsIntegrations.map(v => (
                        <option key={v.id} value={v.id}>{v.name} ({v.type})</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {camLoading ? (
                <div className="verify-loading">
                  <RefreshCw size={18} className="spin" />
                  <p>Loading cameras...</p>
                </div>
              ) : cameras.length === 0 ? (
                <div className="scan-summary empty">
                  <Camera size={20} />
                  <div>
                    <strong>No cameras found.</strong>
                    <p>Verify the VMS integration is working and has cameras available.</p>
                  </div>
                </div>
              ) : (
                <div className="device-results-table-wrap">
                  <table className="device-results-table selectable">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>
                          <input
                            type="checkbox"
                            checked={selected.size === cameras.length && cameras.length > 0}
                            onChange={toggleAll}
                          />
                        </th>
                        <th>Camera</th>
                        <th>Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cameras.map(c => (
                        <tr key={c.id} className={selected.has(c.id) ? 'selected' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(c.id)}
                              onChange={() => toggleOne(c.id)}
                            />
                          </td>
                          <td>{c.name || c.displayName || '—'}</td>
                          <td>{c.model || c.deviceModel || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          <div className="step-actions">
            <button className="site-setup-btn ghost" onClick={handleSkip}>
              <SkipForward size={14} /> Skip for now
            </button>
            <button
              className="site-setup-btn primary"
              onClick={handleEnable}
              disabled={!enabled || selected.size === 0 || submitting}
            >
              {submitting ? 'Enabling...' : `Enable for ${selected.size} Camera${selected.size === 1 ? '' : 's'}`}
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RecordingAssuranceStep;
